import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState } from "react";
import { CalendarBlankIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { getDestinationRideEvents } from "@/lib/eventsApi";

const validHttps = (value) => typeof value === "string" && /^https:\/\/[^\s]+$/i.test(value);

export function DestinationEvents({ destinationId }) {
  const [events, setEvents] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!destinationId) { setEvents([]); setState("empty"); return undefined; }
    const controller = new AbortController();
    setState("loading");
    getDestinationRideEvents(destinationId, { signal: controller.signal })
      .then((items) => {
        if (controller.signal.aborted) return;
        setEvents(items);
        setState(items.length ? "ready" : "empty");
      })
      .catch(() => { if (!controller.signal.aborted) setState("error"); });
    return () => controller.abort();
  }, [destinationId]);

  // Keep destinations with no scheduled events free of empty placeholders.
  if (state === "empty" || state === "loading") return null;

  return (
    <section aria-label={translateLabel("Upcoming events at this destination")} data-testid="destination-events" className="scroll-mt-28 rounded-2xl border border-white/10 bg-[#121214] p-4">
      <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase text-white">
        <CalendarBlankIcon size={20} className="text-[#CCFF00]" /> Upcoming events
      </h2>
      {state === "error" ? (
        <p role="status" className="mt-3 text-sm text-zinc-400">{translateLabel("Events are temporarily unavailable.")}</p>
      ) : (
        <div className="mt-3 space-y-3">
          {events.map((event) => {
            let start = "Date to be confirmed";
            try {
              start = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: event.timeZone || "UTC" }).format(new Date(event.startsAt));
            } catch { /* retain fallback */ }
            const officialUrl = validHttps(event.registrationUrl) ? event.registrationUrl : validHttps(event.sourceUrl) ? event.sourceUrl : null;
            return (
              <article key={event.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs font-semibold text-[#CCFF00]">{start} · {event.category}</p>
                <h3 className="mt-1 text-sm font-bold text-white">{event.title}</h3>
                <p className="mt-1 text-xs text-zinc-400">{event.venueName}, {event.city}</p>
                {officialUrl && <a href={officialUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#CCFF00] hover:underline">{translateLabel("Official event details")} <ArrowSquareOutIcon size={13} /></a>}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
